import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon, CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import type { BillingStatus } from '@/hooks/useBillingStatus'

interface BillingLockoutBannerProps {
  status: BillingStatus
  dismissible?: boolean
}

/**
 * Banner component to display when user has billing issues.
 * Shows warning for past_due, locked out state, or suspension.
 */
export default function BillingLockoutBanner({ status, dismissible = false }: BillingLockoutBannerProps) {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  // Don't show banner for active accounts
  if (status.status === 'active') return null

  // Determine banner type and content
  let bgColor = 'bg-amber-500/10 border-amber-500/20'
  let textColor = 'text-amber-400'
  const icon = <ExclamationTriangleIcon className="w-5 h-5" />
  let title = ''
  let message = ''
  let showPayButton = true

  if (status.status === 'banned') {
    bgColor = 'bg-red-500/10 border-red-500/20'
    textColor = 'text-red-400'
    title = 'Account Banned'
    message = 'Your account has been permanently banned. Please contact support if you believe this is an error.'
    showPayButton = false
  } else if (status.status === 'suspended') {
    bgColor = 'bg-red-500/10 border-red-500/20'
    textColor = 'text-red-400'
    title = 'Account Suspended'
    message = 'Your account has been suspended. Please contact support or resolve outstanding issues to restore access.'
    showPayButton = true
  } else if (status.is_locked_out) {
    bgColor = 'bg-red-500/10 border-red-500/20'
    textColor = 'text-red-400'
    title = status.enforcement_state === 'services_deleted' ? 'Servers Deleted' : 'Servers Powered Off'
    message = status.enforcement_state === 'services_deleted'
      ? 'Your unpaid servers were permanently deleted. Your account and billing history remain available. Check the Backup Vault for retained recovery points.'
      : status.deletion_at
        ? `Your unpaid servers are powered off and will be permanently deleted on ${new Date(status.deletion_at).toLocaleString()} unless payment is received.`
        : 'Your payment is overdue and your servers are restricted. Please update your payment method to restore eligible services.'
  } else if (status.status === 'past_due') {
    title = 'Payment Past Due'
    const graceDays = status.power_off_at
      ? Math.max(0, Math.ceil((new Date(status.power_off_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : 0
    message = graceDays > 0
      ? `Your payment is past due. You have ${graceDays} day${graceDays !== 1 ? 's' : ''} to update your payment method before access is restricted.`
      : 'Your payment is past due. Please update your payment method to avoid service interruption.'
    if (status.hold_until) {
      message += ` An administrative hold is active until ${new Date(status.hold_until).toLocaleString()}.`
    } else if (status.deletion_at) {
      message += ` Unpaid servers will be permanently deleted on ${new Date(status.deletion_at).toLocaleString()}.`
    }
  }

  if (status.backup_deletion_deadline) message += ` Backups have a separate deletion deadline: ${new Date(status.backup_deletion_deadline).toLocaleString()}. Download or delete them in the Backup Vault while you resolve payment.`

  return (
    <div className={`relative flex items-center gap-3 px-4 py-3 border rounded-lg ${bgColor}`}>
      <div className={`flex-shrink-0 ${textColor}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${textColor}`}>
          {title}
        </p>
        <p className="text-sm text-gray-400 mt-0.5">
          {message}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {showPayButton && (
          <Link
            to="/billing-settings"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors"
          >
            <CreditCardIcon className="w-4 h-4" />
            Update Payment
          </Link>
        )}
        {dismissible && status.status === 'past_due' && !status.is_locked_out && (
          <button
            onClick={() => setDismissed(true)}
            className={`p-1 ${textColor} hover:opacity-70 transition-opacity`}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}
